"use client"

import { useRef, useEffect, useState } from "react"
import "./WhyChooseUs.css"
import { TbTruckDelivery } from "react-icons/tb"
import { FaCheckCircle } from "react-icons/fa"
import { MdOutlinePriceCheck } from "react-icons/md"
import { PiSlidersDuotone } from "react-icons/pi"
import { GiBrain } from "react-icons/gi"
import { ImLeaf } from "react-icons/im"
import abm from "../assets/ABM Logo.png"
import iso from "../assets/certs/iso-22000-2018.png"
import inter from "../assets/certs/inter-food-tech.png"
import mart from "../assets/certs/india-mart-trust-seal.png"
import bombay from "../assets/certs/bombay-chamber.png"
import snack from "../assets/certs/snack-bake.png"
import pacmech from "../assets/certs/pac-mech.png"

const WhyChooseUs = () => {
  const sectionRef = useRef(null)
  const carouselRef = useRef(null)
  const touchStartX = useRef(null)
  const touchEndX = useRef(null)
  const [currentSlide, setCurrentSlide] = useState(0)

  const reasons = [
    {
      id: "quality",
      title: "Superior Quality",
      icon: <FaCheckCircle />,
      description: "Our products undergo rigorous quality control to ensure they meet the highest standards.",
    },
    {
      id: "delivery",
      title: "Fast Delivery",
      icon: <TbTruckDelivery />,
      description: "We maintain efficient production and logistics to deliver your orders on time, every time.",
    },
    {
      id: "pricing",
      title: "Competitive Pricing",
      icon: <MdOutlinePriceCheck />,
      description:
        "Our streamlined manufacturing processes allow us to offer high-quality products at competitive prices.",
    },
    {
      id: "customization",
      title: "Customization Options",
      icon: <PiSlidersDuotone />,
      description:
        "We offer extensive customization options to meet your specific branding and functional requirements.",
    },
    {
      id: "expertise",
      title: "Industry Expertise",
      icon: <GiBrain />,
      description:
        "With decades of experience, our team provides expert guidance on the best packaging solutions for your needs.",
    },
    {
      id: "sustainability",
      title: "Sustainability Focus",
      icon: <ImLeaf />,
      description:
        "We're committed to environmentally responsible manufacturing practices and recyclable packaging solutions.",
    },
  ]

  const certifications = [
    { id: "iso22000", name: "ISO 22000:2018", logo: iso  },
    { id: "india-mart", name: "India Mart Trust Seal", logo: mart },
    { id: "interfoodtech", name: "Inter Food Tech", logo: inter },
    { id: "bombaychamber", name: "Bombay Chamber Of Commerce", logo: bombay },
    { id: "snackandbake", name: "Snack & Bake Tech", logo: snack },
    { id: "pacmechex", name: "Pac Mech Ex", logo: pacmech },
    // { id: "haccp", name: "HACCP", logo: "/placeholder.svg?height=80&width=80" },
  ]

  const testimonials = [
    {
      id: 1,
      image: abm,
      text: "ABM Metal Tech has been our trusted packaging partner for over 5 years. Their quality is consistently excellent, and their customer service is outstanding.",
      name: "John Smith",
      title: "Procurement Manager, Food Co.",
    },
    {
      id: 2,
      image: abm,
      text: "The team at ABM Metal Tech always delivers on time. Their dedication to quality and innovation has really helped our business.",
      name: "Jane Doe",
      title: "Operations Head, Tech Supplies Inc.",
    },
    {
      id: 3,
      image: abm,
      text: "Reliable, efficient, and great to work with! ABM Metal Tech has exceeded our expectations every single time.",
      name: "Michael Lee",
      title: "Director, Health Essentials",
    },
  ]

  const totalSlides = testimonials.length
  const slidesToShow = 1

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
          }
        })
      },
      { threshold: 0.1 },
    )

    const animatedElements = sectionRef.current.querySelectorAll(".animate-in")
    animatedElements.forEach((el) => observer.observe(el))

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el))
    }
  }, [])

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides)
  }

  const goToSlide = (index) => {
    setCurrentSlide(index)
  }

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX
  }

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return
    const diffX = touchStartX.current - touchEndX.current
    if (diffX > 50) {
      nextSlide()
    } else if (diffX < -50) {
      prevSlide()
    }
    touchStartX.current = null
    touchEndX.current = null
  }

  return (
    <div className="why-choose-us" ref={sectionRef}>
      <div className="container">
        <h2 className="text-center animate-in">Why Choose Us</h2>
        <p className="why-choose-us-subtitle text-center animate-in">
          Discover the advantages of partnering with ABM Metal Tech for your packaging needs
        </p>

        <div className="reasons-grid">
          {reasons.map((reason, index) => (
            <div className="reason-card animate-in" key={reason.id} style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="reason-icon">{reason.icon}</div>
              <h3>{reason.title}</h3>
              <p>{reason.description}</p>
            </div>
          ))}
        </div>

        <div className="certifications animate-in">
          <h3>Our Certifications</h3>
          <div className="certifications-grid">
            {certifications.map((cert) => (
              <div className="certification" key={cert.id}>
                <img src={cert.logo || "/placeholder.svg"} alt={cert.name} />
                <span>{cert.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="testimonials animate-in">
          <h3>What Our Clients Say</h3>
          <div
            className="testimonials-carousel"
            ref={carouselRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="carousel-wrapper">
              <div
                className="carousel-slides"
                style={{
                  transform: `translateX(-${currentSlide * 100}%)`,
                  transition: "transform 0.5s ease",
                }}
              >
                {testimonials.map((testimonial) => (
                  <div className="carousel-slide" key={testimonial.id}>
                    <div className="testimonial">
                      <div className="testimonial-wrapper">
                        <div className="testimonial-author-image">
                          <img src={testimonial.image} alt={testimonial.name} />
                        </div>
                        <div className="testimonial-text-content">
                          <div className="testimonial-content">
                            <p>{testimonial.text}</p>
                          </div>
                          <div className="testimonial-author-info">
                            <h4>{testimonial.name}</h4>
                            <p>{testimonial.title}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {totalSlides > slidesToShow && (
              <>
                <button className="carousel-prev" onClick={prevSlide}>
                  ❮
                </button>
                <button className="carousel-next" onClick={nextSlide}>
                  ❯
                </button>
                <div className="carousel-dots">
                  {Array.from({ length: totalSlides }).map((_, index) => (
                    <span
                      key={index}
                      className={`carousel-dot ${currentSlide === index ? "active" : ""}`}
                      onClick={() => goToSlide(index)}
                    ></span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default WhyChooseUs
